import React, { useState, useEffect, useCallback } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  X, 
  Edit3, 
  Eye, 
  Copy, 
  Check,
  Download,
  Save,
  RotateCcw,
  Send,
  SendHorizontal
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useToast } from "@/components/hooks/use-toast";
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import { twMerge } from 'tailwind-merge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import type { ChatMessage, ArtifactData } from '@/types';
import { copyAsMarkdown, copyContentToClipboard, copyAsBasicHTML } from '@/utils/copyToClipboard';
import { artifactMarkdownComponents } from '@/components/markdown/artifactComponents';
import QuartoExportButton, { type ExportFormat } from '@/components/QuartoExportButton';
import { useArtifacts } from '@/contexts/ArtifactContext';
import { ArtifactPreview } from './ArtifactPreview';

interface ArtifactPanelProps {
  onArtifactUpdate?: (artifactId: string, newContent: string) => void;
  className?: string;
  hideToggleButton?: boolean; // New prop to hide the floating toggle button
  inline?: boolean; // New prop for inline layout instead of fixed positioning
}

const ArtifactPanel: React.FC<ArtifactPanelProps> = ({ 
  onArtifactUpdate,
  className,
  hideToggleButton = false,
  inline = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editedType, setEditedType] = useState('');
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  
  // Use artifact context instead of extracting from messages
  const { 
    artifacts, 
    activeArtifactId, 
    setActiveArtifactId, 
    isPanelOpen, 
    setPanelOpen,
    updateArtifact,
    toggleArtifactInclusion
  } = useArtifacts();



  // Reset editing state when active artifact changes
  useEffect(() => {
    setIsEditing(false);
    setEditedContent('');
    setEditedType('');
  }, [activeArtifactId]);

  // Get current artifact
  const currentArtifact = activeArtifactId ? artifacts.get(activeArtifactId) : null;

  // Handle edit mode toggle
  const handleEditToggle = useCallback(() => {
    if (currentArtifact) {
      if (isEditing) {
        // Save changes (both content and type)
        const contentChanged = editedContent !== currentArtifact.content;
        const typeChanged = editedType !== currentArtifact.type;
        
        if (contentChanged || typeChanged) {
          // Create updated artifact with new content and type
          const updatedArtifact = {
            ...currentArtifact,
            content: editedContent,
            type: editedType
          };
          
          // Update in context
          updateArtifact(currentArtifact.id, editedContent, editedType);
          onArtifactUpdate?.(currentArtifact.id, editedContent);
          
          toast({
            title: "Artifact Updated",
            description: `"${currentArtifact.title}" has been saved${typeChanged ? ` (type changed to ${editedType})` : ''}.`,
          });
        }
        setIsEditing(false);
      } else {
        setEditedContent(currentArtifact.content);
        setEditedType(currentArtifact.type);
        setIsEditing(true);
      }
    }
  }, [currentArtifact, isEditing, editedContent, editedType, updateArtifact, onArtifactUpdate, toast]);

  // Handle discard changes
  const handleDiscardChanges = useCallback(() => {
    if (currentArtifact) {
      setEditedContent(currentArtifact.content);
      setEditedType(currentArtifact.type);
      setIsEditing(false);
    }
  }, [currentArtifact]);

  // Handle copy as rendered content with smart copy strategy
  const handleCopyRendered = useCallback(async () => {
    if (!currentArtifact) return;
    
    const contentToCopy = isEditing ? editedContent : currentArtifact.content;
    const artifactType = isEditing ? editedType : currentArtifact.type;
    
    try {
      // Define which types should use simple text copy vs rich copy
      const simpleTextTypes = [
        'javascript', 'js', 'typescript', 'ts', 'python', 'py', 
        'css', 'sql', 'bash', 'sh', 'json', 'yaml', 'yml', 'xml'
      ];
      
      const richContentTypes = [
        'markdown', 'md', 'html', 'react', 'jsx', 'tsx'
      ];
      
      const lowerType = artifactType.toLowerCase();
      
      // Special handling for text/txt types
      if (lowerType === 'text' || lowerType === 'txt') {
        // Check if content looks like markdown
        const hasMarkdownTable = contentToCopy.includes('|') && contentToCopy.includes('---');
        const hasMarkdownHeaders = /^#{1,6}\s/m.test(contentToCopy) || contentToCopy.includes('**');
        
        if (hasMarkdownTable || hasMarkdownHeaders) {
          // Treat as markdown for rich copy
          // Fall through to rich content handling below
        } else {
          // Plain text copy
          await navigator.clipboard.writeText(contentToCopy);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
          toast({
            title: "Copied to Clipboard",
            description: `"${currentArtifact.title}" copied as plain text.`,
          });
          return;
        }
      }
      
      // Simple text copy for code artifacts - users usually want the raw code
      if (simpleTextTypes.includes(lowerType)) {
        await navigator.clipboard.writeText(contentToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast({
          title: "Copied to Clipboard",
          description: `"${currentArtifact.title}" source code copied as plain text.`,
        });
        return;
      }
      
      // Rich copy for content artifacts - users want the rendered output with formatting
      if (richContentTypes.includes(lowerType)) {
        const artifactContainer = document.querySelector(`[data-artifact-content="${currentArtifact.id}"]`) as HTMLElement;
        
        if (artifactContainer) {
          const success = await copyContentToClipboard({
            messageContainer: artifactContainer,
            cleanedContent: contentToCopy,
            markdownComponents: artifactMarkdownComponents
          });
          
          if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast({
              title: "Copied to Clipboard",
              description: `"${currentArtifact.title}" content copied with rich formatting.`,
            });
            return;
          }
        }
        
        // Fallback to basic HTML copy for rich content
        const success = await copyAsBasicHTML(contentToCopy, artifactMarkdownComponents);
        if (success) {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
          toast({
            title: "Copied to Clipboard",
            description: `"${currentArtifact.title}" content copied with basic formatting.`,
          });
          return;
        }
      }
      
      // Final fallback to plain text for any unknown types
      await navigator.clipboard.writeText(contentToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: "Copied to Clipboard",
        description: `"${currentArtifact.title}" copied as plain text.`,
      });
      
    } catch (err) {
      console.error('Failed to copy artifact content:', err);
      toast({
        title: "Copy Failed",
        description: "Could not copy to clipboard.",
        variant: "destructive",
      });
    }
  }, [currentArtifact, isEditing, editedContent, editedType]);

  // Handle download
  const handleDownload = useCallback(() => {
    if (currentArtifact) {
      const contentToDownload = isEditing ? editedContent : currentArtifact.content;
      const blob = new Blob([contentToDownload], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentArtifact.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.${getFileExtension(currentArtifact.type)}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Download Started",
        description: `"${currentArtifact.title}" download initiated.`,
      });
    }
  }, [currentArtifact, isEditing, editedContent, toast]);

  // Get file extension based on artifact type
  const getFileExtension = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'markdown':
      case 'md':
        return 'md';
      case 'html':
        return 'html';
      case 'javascript':
      case 'js':
        return 'js';
      case 'typescript':
      case 'ts':
        return 'ts';
      case 'python':
      case 'py':
        return 'py';
      case 'json':
        return 'json';
      case 'yaml':
      case 'yml':
        return 'yml';
      case 'css':
        return 'css';
      case 'svg':
        return 'svg';
      default:
        return 'txt';
    }
  };

  // Don't render if no artifacts
  if (artifacts.size === 0) {
    return null;
  }

  const artifactList = Array.from(artifacts.values());

  return (
    <>
      {/* Toggle button when panel is closed - only show if not hidden */}
      {!hideToggleButton && !isPanelOpen && (
        <Button
          variant="outline"
          size="sm"
          className="fixed top-1/2 right-4 z-50 transform -translate-y-1/2 shadow-lg"
          onClick={() => setPanelOpen(true)}
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="ml-1">Artifacts ({artifacts.size})</span>
        </Button>
      )}

      {/* Artifact panel */}
      <div 
        className={twMerge(
          inline 
            ? 'h-full bg-background flex flex-col' 
            : 'fixed top-0 bottom-0 right-0 z-30 w-full sm:w-[40%] min-w-[320px] sm:min-w-[400px] max-w-[600px] bg-background border-l shadow-lg transition-transform duration-300 ease-in-out flex flex-col',
          inline ? '' : (isPanelOpen ? 'translate-x-0' : 'translate-x-full'),
          className
        )}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        {/* Panel header */}
        <div className="flex items-center justify-between p-3 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex items-center space-x-2">
            <h3 className="text-sm font-medium">Artifacts</h3>
            <Badge variant="secondary" className="text-xs">
              {artifacts.size}
            </Badge>
          </div>
          <div className="flex items-center space-x-1">
            {currentArtifact && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyRendered}
                  title="Copy content with formatting"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDownload}
                  title="Download file"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPanelOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Artifact selector - always shown, acts as primary heading */}
        <div className="p-3 border-b">
          <Select value={activeArtifactId || undefined} onValueChange={setActiveArtifactId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select an artifact">
                {currentArtifact && (
                  <div className="flex items-center justify-between w-full">
                    <div className="flex flex-col items-start text-left">
                      <span className="font-medium truncate">{currentArtifact.title}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">ID: {currentArtifact.id}</span>
                        {currentArtifact.includeInApi !== false && (
                          <Badge variant="secondary" className="text-xs gap-1">
                            <SendHorizontal className="h-2 w-2" />
                            Sent to AI
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Badge variant="outline" className="ml-2 text-xs">
                      {currentArtifact.type}
                    </Badge>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {artifactList.map((artifact: ArtifactData) => (
                <SelectItem key={artifact.id} value={artifact.id}>
                  <div className="flex items-center justify-between w-full">
                    <div className="flex flex-col items-start">
                      <span className="font-medium truncate">{artifact.title}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">ID: {artifact.id}</span>
                        {artifact.includeInApi !== false && (
                          <Badge variant="secondary" className="text-xs gap-1">
                            <SendHorizontal className="h-2 w-2" />
                            Included
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Badge variant="outline" className="ml-2 text-xs">
                      {artifact.type}
                    </Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Include in API toggle */}
          {currentArtifact && (
            <div className="mt-2">
              <Button
                variant={currentArtifact.includeInApi !== false ? "secondary" : "outline"}
                size="sm"
                className="w-full justify-between"
                onClick={() => toggleArtifactInclusion(currentArtifact.id)}
              >
                <span className="flex items-center gap-2">
                  <Send className="h-3 w-3" />
                  {currentArtifact.includeInApi !== false ? "Included in AI context" : "Not included in AI context"}
                </span>
                <span className="text-xs text-muted-foreground">
                  Click to toggle
                </span>
              </Button>
              <p className="text-xs text-muted-foreground mt-1 px-1">
                When included, this artifact will be sent with your messages to provide context to the AI assistant.
              </p>
            </div>
          )}
        </div>

        {/* Current artifact content */}
        {currentArtifact && (
          <div className="flex-1 flex flex-col">

            {/* Edit/preview tabs */}
            <Tabs defaultValue="preview" className="flex-1 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b flex-shrink-0">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="preview" className="flex items-center space-x-1">
                    <Eye className="h-3 w-3" />
                    <span>Preview</span>
                  </TabsTrigger>
                  <TabsTrigger value="edit" className="flex items-center space-x-1">
                    <Edit3 className="h-3 w-3" />
                    <span>Edit</span>
                  </TabsTrigger>
                </TabsList>
                
                {isEditing && (
                  <div className="flex items-center space-x-1 ml-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDiscardChanges}
                      title="Discard changes"
                    >
                      <RotateCcw className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleEditToggle}
                      title="Save changes"
                    >
                      <Save className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Preview tab */}
              <TabsContent value="preview" className="flex-1 overflow-hidden m-0 p-0">
                <div 
                  className="h-full overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
                  style={{ 
                    maxHeight: 'calc(100vh - 200px)',
                    height: '100%'
                  }}
                  onWheel={(e) => e.stopPropagation()}
                  onScroll={(e) => e.stopPropagation()}
                >
                  <div 
                    className="p-3"
                    data-artifact-content={currentArtifact.id}
                  >
                    <div className="prose prose-sm max-w-none">
                      <Markdown
                        remarkPlugins={[remarkGfm, remarkMath]}
                        rehypePlugins={[rehypeRaw, rehypeKatex]}
                        components={artifactMarkdownComponents}
                        skipHtml={false}
                      >
                        {(() => {
                          const content = isEditing ? editedContent : currentArtifact.content;
                          const artifactType = isEditing ? editedType : currentArtifact.type;
                          
                          // For artifacts with previews, don't show code - they can see it in Edit tab
                          // and the interactive preview below is more valuable
                          const isReactComponent = ['react', 'jsx', 'tsx'].includes(artifactType.toLowerCase());
                          const isJavaScriptWithPreview = artifactType.toLowerCase() === 'javascript' || artifactType.toLowerCase() === 'js';
                          const isHtmlWithPreview = artifactType.toLowerCase() === 'html';
                          
                          if (isReactComponent || isJavaScriptWithPreview || isHtmlWithPreview) {
                            return ''; // No code display - just show interactive preview below
                          }
                          
                          // Check if this is a code artifact type
                          const codeTypes = ['javascript', 'typescript', 'python', 'html', 'css', 'json', 'yaml', 'sql', 'bash', 'js', 'ts', 'py', 'sh', 'shell', 'xml', 'java', 'c', 'cpp', 'go', 'rust', 'php', 'ruby', 'swift'];
                          
                          if (codeTypes.includes(artifactType.toLowerCase())) {
                            // Wrap in markdown code block for syntax highlighting
                            return `\`\`\`${artifactType.toLowerCase()}\n${content}\n\`\`\``;
                          }
                          
                          // For markdown type, render as-is
                          if (artifactType.toLowerCase() === 'markdown' || artifactType.toLowerCase() === 'md') {
                            return content;
                          }
                          
                          // For plain text, detect if it contains markdown table syntax
                          if (artifactType.toLowerCase() === 'text' || artifactType.toLowerCase() === 'txt') {
                            // Check if content looks like a markdown table
                            const hasMarkdownTable = content.includes('|') && content.includes('---');
                            const hasMarkdownHeaders = /^#{1,6}\s/.test(content) || content.includes('**');
                            
                            if (hasMarkdownTable || hasMarkdownHeaders) {
                              // Render as markdown to preserve table formatting
                              return content;
                            } else {
                              // Wrap plain text in code block to preserve formatting
                              return `\`\`\`\n${content}\n\`\`\``;
                            }
                          }
                          
                          // For all other types, render as markdown
                          return content;
                        })()}
                      </Markdown>
                    </div>
                    
                    {/* Interactive preview for HTML/React artifacts */}
                    <ArtifactPreview
                      content={isEditing ? editedContent : currentArtifact.content}
                      type={isEditing ? editedType : currentArtifact.type}
                      title={currentArtifact.title}
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Edit tab */}
              <TabsContent value="edit" className="flex-1 overflow-hidden">
                <div className="h-full p-3 flex flex-col">
                  {/* Type selector */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                      Artifact Type
                    </label>
                    <Select
                      value={isEditing ? editedType : currentArtifact.type}
                      onValueChange={(value) => {
                        if (!isEditing) {
                          setEditedContent(currentArtifact.content); // Initialize content
                          setEditedType(value);
                          setIsEditing(true);
                        } else {
                          setEditedType(value);
                        }
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="markdown">Markdown (.md)</SelectItem>
                        <SelectItem value="html">HTML (.html)</SelectItem>
                        <SelectItem value="javascript">JavaScript (.js)</SelectItem>
                        <SelectItem value="typescript">TypeScript (.ts)</SelectItem>
                        <SelectItem value="react">React (.jsx)</SelectItem>
                        <SelectItem value="jsx">JSX (.jsx)</SelectItem>
                        <SelectItem value="tsx">TSX (.tsx)</SelectItem>
                        <SelectItem value="python">Python (.py)</SelectItem>
                        <SelectItem value="css">CSS (.css)</SelectItem>
                        <SelectItem value="json">JSON (.json)</SelectItem>
                        <SelectItem value="yaml">YAML (.yml)</SelectItem>
                        <SelectItem value="xml">XML (.xml)</SelectItem>
                        <SelectItem value="sql">SQL (.sql)</SelectItem>
                        <SelectItem value="bash">Bash (.sh)</SelectItem>
                        <SelectItem value="text">Plain Text (.txt)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <ScrollArea className="flex-1">
                    <Textarea
                      value={isEditing ? editedContent : currentArtifact.content}
                      onChange={(e) => {
                        if (!isEditing) {
                          setEditedType(currentArtifact.type); // Initialize type
                          setEditedContent(e.target.value);
                          setIsEditing(true);
                        } else {
                          setEditedContent(e.target.value);
                        }
                      }}
                      className="min-h-[calc(100vh-400px)] resize-none font-mono text-sm border-0 focus-visible:ring-0 p-0"
                      placeholder="Edit artifact content..."
                    />
                  </ScrollArea>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </>
  );
};

export default ArtifactPanel;